import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Wallet, ArrowUpRight, ArrowDownRight, Plus, Minus, Copy, 
  RefreshCw, Bitcoin, DollarSign, CreditCard, Building2, 
  QrCode, Shield, Clock, CheckCircle2, AlertCircle, 
  TrendingUp, Coins, Landmark, RotateCcw, ArrowLeftRight,
  Banknote, TestTube
} from 'lucide-react';

import { API } from '@/config/api';

// Supported assets
const cryptoAssets = [
  { symbol: 'BTC', name: 'Bitcoin', icon: '₿', color: '#F7931A', network: 'Bitcoin' },
  { symbol: 'ETH', name: 'Ethereum', icon: 'Ξ', color: '#627EEA', network: 'ERC-20' },
  { symbol: 'USDT', name: 'Tether', icon: '₮', color: '#26A17B', network: 'TRC-20/ERC-20' },
  { symbol: 'USDC', name: 'USD Coin', icon: '$', color: '#2775CA', network: 'ERC-20' },
  { symbol: 'SOL', name: 'Solana', icon: '◎', color: '#9945FF', network: 'Solana' },
  { symbol: 'BNB', name: 'BNB', icon: 'B', color: '#F3BA2F', network: 'BEP-20' },
];

const fiatCurrencies = [
  { symbol: 'USD', name: 'US Dollar', icon: '$' },
  { symbol: 'EUR', name: 'Euro', icon: '€' },
  { symbol: 'GBP', name: 'British Pound', icon: '£' },
];

// Mock wallet addresses for demo
const walletAddresses = {
  BTC: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
  ETH: '0x742d35Cc6634C0532925a3b844Bc9e7595f7BDEF',
  USDT: 'TN4qJBypchKmVQzKYCKrLqYrjFHgXbvDEF',
  USDC: '0x742d35Cc6634C0532925a3b844Bc9e7595f7BDEF',
  SOL: '7EcDhSYGxXyscszYEp35KHN8vvw3svAuLKTzXwCFLtV',
  BNB: 'bnb1grpf0955h0ykzq3ar5nmum7y6gdfl6lxfn46h2',
};

export default function WalletPage() {
  const { user } = useAuth();
  const [walletMode, setWalletMode] = useState('real'); // 'real' or 'paper'
  const [activeTab, setActiveTab] = useState('overview');
  const [realWallet, setRealWallet] = useState(null);
  const [paperWallet, setPaperWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cryptoPrices, setCryptoPrices] = useState({});
  
  // Modal states
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [paperDepositModalOpen, setPaperDepositModalOpen] = useState(false);
  
  // Form states
  const [selectedAsset, setSelectedAsset] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [selectedFiat, setSelectedFiat] = useState('USD');
  const [buyAmount, setBuyAmount] = useState('');
  const [paperDepositAmount, setPaperDepositAmount] = useState('');
  
  // Get current wallet based on mode
  const currentWallet = walletMode === 'real' ? realWallet : paperWallet;
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchRealWallet(),
      fetchPaperWallet(),
      fetchTransactions(),
      fetchCryptoPrices()
    ]);
    setLoading(false);
  };
  
  const fetchCryptoPrices = async () => {
    try {
      const response = await fetch(`${API}/market/crypto`);
      if (response.ok) {
        const data = await response.json();
        const prices = {};
        data.forEach(coin => {
          prices[coin.symbol.toUpperCase()] = coin.current_price;
        });
        setCryptoPrices(prices);
      }
    } catch (error) {
      console.error('Price fetch error:', error);
    }
  };
  
  const fetchRealWallet = async () => {
    try {
      const response = await fetch(`${API}/wallet`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRealWallet(data);
      }
    } catch (error) {
      console.error('Real wallet fetch error:', error);
    }
  };
  
  const fetchPaperWallet = async () => {
    try {
      const response = await fetch(`${API}/wallet/paper`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPaperWallet(data);
      }
    } catch (error) {
      console.error('Paper wallet fetch error:', error);
    }
  };
  
  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API}/wallet/transactions?wallet_type=all`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error('Transactions fetch error:', error);
    }
  };
  
  const handleDeposit = async () => {
    toast.success(`Deposit address generated for ${selectedAsset}`);
    // In production, this would create a deposit request
  };
  
  const handleWithdraw = async () => {
    if (!amount || !withdrawAddress) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      const response = await fetch(`${API}/wallet/withdraw?wallet_type=${walletMode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset: selectedAsset,
          amount: parseFloat(amount),
          address: withdrawAddress
        })
      });
      
      if (response.ok) {
        toast.success(`Withdrawal request submitted for ${amount} ${selectedAsset}`);
        setWithdrawModalOpen(false);
        setAmount('');
        setWithdrawAddress('');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Withdrawal failed');
      }
    } catch (error) {
      toast.error('Withdrawal request failed');
    }
  };
  
  const handleBuyCrypto = async () => {
    if (!buyAmount) {
      toast.error('Please enter an amount');
      return;
    }
    
    try {
      const response = await fetch(`${API}/wallet/buy?wallet_type=${walletMode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset: selectedAsset,
          fiat_currency: selectedFiat,
          fiat_amount: parseFloat(buyAmount)
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Purchased ${result.amount.toFixed(8)} ${selectedAsset} for $${buyAmount}`);
        setBuyModalOpen(false);
        setBuyAmount('');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Purchase failed');
      }
    } catch (error) {
      toast.error('Purchase failed');
    }
  };
  
  const handlePaperDeposit = async () => {
    if (!paperDepositAmount) {
      toast.error('Please enter an amount');
      return;
    }
    
    try {
      const response = await fetch(`${API}/wallet/paper/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset: selectedAsset,
          amount: parseFloat(paperDepositAmount)
        })
      });
      
      if (response.ok) {
        toast.success(`Added ${paperDepositAmount} ${selectedAsset} to paper wallet`);
        setPaperDepositModalOpen(false);
        setPaperDepositAmount('');
        fetchPaperWallet();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Deposit failed');
      }
    } catch (error) {
      toast.error('Deposit failed');
    }
  };
  
  const handleResetPaperWallet = async () => {
    try {
      const response = await fetch(`${API}/wallet/paper/reset`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Paper trading wallet reset to $100,000');
        fetchPaperWallet();
      } else {
        toast.error('Failed to reset wallet');
      }
    } catch (error) {
      toast.error('Reset failed');
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Address copied to clipboard');
  };
  
  const formatCurrency = (value, decimals = 2) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value || 0);
  };
  
  const formatCrypto = (value, symbol) => {
    const decimals = ['BTC', 'ETH'].includes(symbol) ? 8 : 2;
    return `${parseFloat(value || 0).toFixed(decimals)} ${symbol}`;
  };
  
  const getAssetPrice = (symbol) => {
    return cryptoPrices[symbol] || {
      BTC: 97500, ETH: 3450, USDT: 1, USDC: 1, SOL: 198, BNB: 715
    }[symbol] || 0;
  };
  
  const getAssetValue = (symbol, amount) => {
    return (amount || 0) * getAssetPrice(symbol);
  };
  
  const filteredTransactions = transactions.filter(tx => 
    walletMode === 'all' || tx.wallet_type === walletMode
  );
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20" data-testid="wallet-page">
      {/* Header */}
      <div className="border-b border-[#1c1814]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div>
              <h1 className="font-display text-3xl text-white flex items-center gap-3">
                <Wallet className="h-8 w-8 text-[#D4AF37]" />
                Wallet
              </h1>
              <p className="text-gray-400 mt-1">Manage your crypto and fiat assets</p>
            </div>
            
            {/* Wallet Mode Toggle */}
            <div className="flex items-center gap-4">
              <div className="bg-[#14120f] p-1 rounded-lg border border-[#1c1814] flex">
                <button
                  onClick={() => setWalletMode('real')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                    walletMode === 'real' 
                      ? 'bg-[#D4AF37] text-black' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid="real-wallet-toggle"
                >
                  <Banknote className="h-4 w-4" />
                  Real Wallet
                </button>
                <button
                  onClick={() => setWalletMode('paper')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                    walletMode === 'paper' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid="paper-wallet-toggle"
                >
                  <TestTube className="h-4 w-4" />
                  Paper Trading
                </button>
              </div>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mt-4">
            {walletMode === 'real' ? (
              <>
                <Button 
                  onClick={() => setDepositModalOpen(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="deposit-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Deposit
                </Button>
                <Button 
                  onClick={() => setWithdrawModalOpen(true)}
                  variant="outline"
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  data-testid="withdraw-btn"
                >
                  <Minus className="h-4 w-4 mr-2" />
                  Withdraw
                </Button>
                <Button 
                  onClick={() => setBuyModalOpen(true)}
                  className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
                  data-testid="buy-crypto-btn"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Buy Crypto
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => setPaperDepositModalOpen(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="paper-deposit-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Paper Funds
                </Button>
                <Button 
                  onClick={handleResetPaperWallet}
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  data-testid="reset-paper-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset to $100k
                </Button>
              </>
            )}
            <Button 
              onClick={() => setTransferModalOpen(true)}
              variant="outline"
              className="border-gray-600 text-gray-400 hover:text-white"
              data-testid="swap-btn"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Swap
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Paper Trading Notice */}
        {walletMode === 'paper' && (
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3">
            <TestTube className="h-6 w-6 text-blue-400 flex-shrink-0" />
            <div>
              <p className="text-blue-400 font-medium">Paper Trading Mode</p>
              <p className="text-blue-400/70 text-sm">Practice trading with virtual funds. No real money at risk.</p>
            </div>
          </div>
        )}
        
        {/* Portfolio Overview Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/20' : 'bg-[#14120f]'}`}>
            <CardContent className="p-6">
              <p className="text-gray-400 text-sm mb-1">Total Balance</p>
              <p className="font-display text-3xl text-white">
                {formatCurrency(currentWallet?.total_usd || 0)}
              </p>
              <Badge className={walletMode === 'paper' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}>
                {walletMode === 'paper' ? 'Paper' : 'Real'}
              </Badge>
            </CardContent>
          </Card>
          
          <Card className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/20' : 'bg-[#14120f]'}`}>
            <CardContent className="p-6">
              <p className="text-gray-400 text-sm mb-1">Available</p>
              <p className="font-display text-2xl text-white">
                {formatCurrency(currentWallet?.available_usd || currentWallet?.total_usd || 0)}
              </p>
              <p className="text-gray-500 text-sm mt-1">Ready to trade</p>
            </CardContent>
          </Card>
          
          <Card className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/20' : 'bg-[#14120f]'}`}>
            <CardContent className="p-6">
              <p className="text-gray-400 text-sm mb-1">In Orders</p>
              <p className="font-display text-2xl text-[#D4AF37]">
                {formatCurrency(currentWallet?.in_orders || 0)}
              </p>
              <p className="text-gray-500 text-sm mt-1">Open positions</p>
            </CardContent>
          </Card>
          
          <Card className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/20' : 'bg-[#14120f]'}`}>
            <CardContent className="p-6">
              <p className="text-gray-400 text-sm mb-1">Assets</p>
              <p className="font-display text-2xl text-white">
                {Object.entries(currentWallet?.balances || {}).filter(([k, v]) => v > 0).length}
              </p>
              <p className="text-gray-500 text-sm mt-1">Active holdings</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#14120f] border border-[#1c1814] p-1">
            <TabsTrigger 
              value="overview"
              className={`data-[state=active]:text-black ${walletMode === 'paper' ? 'data-[state=active]:bg-blue-500' : 'data-[state=active]:bg-[#D4AF37]'}`}
            >
              Assets
            </TabsTrigger>
            <TabsTrigger 
              value="crypto"
              className={`data-[state=active]:text-black ${walletMode === 'paper' ? 'data-[state=active]:bg-blue-500' : 'data-[state=active]:bg-[#D4AF37]'}`}
            >
              <Bitcoin className="h-4 w-4 mr-2" />
              Crypto
            </TabsTrigger>
            <TabsTrigger 
              value="fiat"
              className={`data-[state=active]:text-black ${walletMode === 'paper' ? 'data-[state=active]:bg-blue-500' : 'data-[state=active]:bg-[#D4AF37]'}`}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Fiat
            </TabsTrigger>
            <TabsTrigger 
              value="history"
              className={`data-[state=active]:text-black ${walletMode === 'paper' ? 'data-[state=active]:bg-blue-500' : 'data-[state=active]:bg-[#D4AF37]'}`}
            >
              <Clock className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>
          
          {/* Assets Overview */}
          <TabsContent value="overview">
            <Card className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/10' : 'bg-[#14120f]'}`}>
              <CardHeader className="border-b border-[#1c1814]">
                <CardTitle className="text-white flex items-center justify-between">
                  <span>All Assets</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={fetchAllData}
                    className="text-gray-400 hover:text-white"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-[#1c1814]">
                  {/* Crypto Assets */}
                  {cryptoAssets.map((asset) => {
                    const balance = currentWallet?.balances?.[asset.symbol] || 0;
                    const value = getAssetValue(asset.symbol, balance);
                    
                    return (
                      <div key={asset.symbol} className="p-4 flex items-center justify-between hover:bg-[#1c1814]/50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold"
                            style={{ backgroundColor: `${asset.color}20`, color: asset.color }}
                          >
                            {asset.icon}
                          </div>
                          <div>
                            <p className="text-white font-medium">{asset.name}</p>
                            <p className="text-gray-500 text-sm">{asset.symbol}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-mono">{formatCrypto(balance, asset.symbol)}</p>
                          <p className="text-gray-500 text-sm">{formatCurrency(value)}</p>
                        </div>
                        <div className="flex gap-2">
                          {walletMode === 'real' ? (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => { setSelectedAsset(asset.symbol); setDepositModalOpen(true); }}
                                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                              >
                                Deposit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => { setSelectedAsset(asset.symbol); setWithdrawModalOpen(true); }}
                                className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                              >
                                Withdraw
                              </Button>
                            </>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setSelectedAsset(asset.symbol); setPaperDepositModalOpen(true); }}
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                            >
                              Add Funds
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Fiat Currencies */}
                  {fiatCurrencies.map((currency) => {
                    const balance = currentWallet?.balances?.[currency.symbol] || 0;
                    
                    return (
                      <div key={currency.symbol} className="p-4 flex items-center justify-between hover:bg-[#1c1814]/50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-xl font-bold text-emerald-400">
                            {currency.icon}
                          </div>
                          <div>
                            <p className="text-white font-medium">{currency.name}</p>
                            <p className="text-gray-500 text-sm">{currency.symbol}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-mono">{currency.icon}{balance.toFixed(2)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setBuyModalOpen(true)}
                            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                          >
                            Add Funds
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Crypto Tab */}
          <TabsContent value="crypto">
            <div className="grid lg:grid-cols-2 gap-6">
              {cryptoAssets.map((asset) => {
                const balance = currentWallet?.balances?.[asset.symbol] || 0;
                const value = getAssetValue(asset.symbol, balance);
                const price = getAssetPrice(asset.symbol);
                
                return (
                  <Card key={asset.symbol} className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/10' : 'bg-[#14120f]'}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold"
                            style={{ backgroundColor: `${asset.color}20`, color: asset.color }}
                          >
                            {asset.icon}
                          </div>
                          <div>
                            <p className="text-white font-medium text-lg">{asset.name}</p>
                            <p className="text-gray-500">{asset.symbol}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="border-gray-600 text-gray-400">
                          {asset.network}
                        </Badge>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Balance</span>
                          <span className="text-white font-mono">{formatCrypto(balance, asset.symbol)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Value</span>
                          <span className="text-white">{formatCurrency(value)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Price</span>
                          <span className="text-[#D4AF37]">{formatCurrency(price)}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4 pt-4 border-t border-[#1c1814]">
                        {walletMode === 'real' ? (
                          <>
                            <Button 
                              size="sm" 
                              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                              onClick={() => { setSelectedAsset(asset.symbol); setDepositModalOpen(true); }}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Deposit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1 border-[#D4AF37]/50 text-[#D4AF37]"
                              onClick={() => { setSelectedAsset(asset.symbol); setWithdrawModalOpen(true); }}
                            >
                              <Minus className="h-4 w-4 mr-1" />
                              Withdraw
                            </Button>
                          </>
                        ) : (
                          <Button 
                            size="sm" 
                            className="flex-1 bg-blue-500 hover:bg-blue-600"
                            onClick={() => { setSelectedAsset(asset.symbol); setPaperDepositModalOpen(true); }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Funds
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          
          {/* Fiat Tab */}
          <TabsContent value="fiat">
            <Card className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/10' : 'bg-[#14120f]'}`}>
              <CardHeader>
                <CardTitle className="text-white">Fiat Balances</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {fiatCurrencies.map((currency) => {
                    const balance = currentWallet?.balances?.[currency.symbol] || 0;
                    
                    return (
                      <Card key={currency.symbol} className="bg-[#1c1814] border-[#252540]">
                        <CardContent className="p-4 text-center">
                          <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-2xl mx-auto mb-3">
                            <span className="text-emerald-400">{currency.icon}</span>
                          </div>
                          <p className="text-gray-400 text-sm">{currency.name}</p>
                          <p className="text-white font-display text-2xl mt-1">
                            {currency.icon}{balance.toFixed(2)}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                
                <div className="p-6 bg-[#1c1814] rounded-lg">
                  <h3 className="text-white font-medium mb-4">Add Funds</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                      onClick={() => setBuyModalOpen(true)}
                    >
                      <div className="text-center">
                        <CreditCard className="h-8 w-8 mx-auto mb-2 text-[#D4AF37]" />
                        <p className="text-white">Card Payment</p>
                        <p className="text-gray-500 text-xs">Visa, Mastercard</p>
                      </div>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                    >
                      <div className="text-center">
                        <Building2 className="h-8 w-8 mx-auto mb-2 text-[#D4AF37]" />
                        <p className="text-white">Bank Transfer</p>
                        <p className="text-gray-500 text-xs">ACH, Wire</p>
                      </div>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                    >
                      <div className="text-center">
                        <Landmark className="h-8 w-8 mx-auto mb-2 text-[#D4AF37]" />
                        <p className="text-white">Apple Pay</p>
                        <p className="text-gray-500 text-xs">Instant</p>
                      </div>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Transaction History */}
          <TabsContent value="history">
            <Card className={`border-[#1c1814] ${walletMode === 'paper' ? 'bg-blue-900/10' : 'bg-[#14120f]'}`}>
              <CardHeader className="border-b border-[#1c1814]">
                <CardTitle className="text-white">Transaction History</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {filteredTransactions.length > 0 ? (
                  <div className="divide-y divide-[#1c1814]">
                    {filteredTransactions.map((tx, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-full ${
                            tx.type === 'deposit' || tx.type === 'purchase' ? 'bg-emerald-500/20' : 
                            tx.type === 'swap' ? 'bg-blue-500/20' : 'bg-red-500/20'
                          }`}>
                            {tx.type === 'deposit' || tx.type === 'purchase' ? (
                              <ArrowDownRight className="h-5 w-5 text-emerald-400" />
                            ) : tx.type === 'swap' ? (
                              <ArrowLeftRight className="h-5 w-5 text-blue-400" />
                            ) : (
                              <ArrowUpRight className="h-5 w-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium capitalize">{tx.type}</p>
                            <div className="flex items-center gap-2">
                              <p className="text-gray-500 text-sm">{tx.asset || tx.from_asset} • {tx.date}</p>
                              <Badge variant="outline" className={tx.wallet_type === 'paper' ? 'border-blue-500 text-blue-400 text-xs' : 'border-emerald-500 text-emerald-400 text-xs'}>
                                {tx.wallet_type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={
                            tx.type === 'deposit' || tx.type === 'purchase' ? 'text-emerald-400' : 
                            tx.type === 'swap' ? 'text-blue-400' : 'text-red-400'
                          }>
                            {tx.type === 'deposit' || tx.type === 'purchase' ? '+' : tx.type === 'withdrawal' ? '-' : ''}
                            {tx.amount?.toFixed(8) || tx.from_amount?.toFixed(8)} {tx.asset || tx.from_asset}
                          </p>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              tx.status === 'completed' ? 'border-emerald-500 text-emerald-400' :
                              tx.status === 'pending' ? 'border-yellow-500 text-yellow-400' :
                              'border-red-500 text-red-400'
                            }`}
                          >
                            {tx.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Clock className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No transactions yet</p>
                    <p className="text-gray-600 text-sm">Your transaction history will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Deposit Modal (Real Wallet) */}
      <Dialog open={depositModalOpen} onOpenChange={setDepositModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-400" />
              Deposit {selectedAsset}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Send {selectedAsset} to your wallet address below
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-gray-400 text-sm">Select Asset</label>
              <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                <SelectTrigger className="bg-[#1c1814] border-[#252540] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1814] border-[#252540]">
                  {cryptoAssets.map((asset) => (
                    <SelectItem key={asset.symbol} value={asset.symbol} className="text-white">
                      {asset.name} ({asset.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-4 bg-[#1c1814] rounded-lg">
              <p className="text-gray-400 text-sm mb-2">Your {selectedAsset} Address</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-white text-sm bg-[#0a0a0f] p-3 rounded break-all">
                  {walletAddresses[selectedAsset]}
                </code>
                <Button 
                  size="icon" 
                  variant="ghost"
                  onClick={() => copyToClipboard(walletAddresses[selectedAsset])}
                  className="text-[#D4AF37]"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
              <p className="text-yellow-500 text-sm">
                Only send {selectedAsset} to this address. Sending other assets may result in permanent loss.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-32 h-32 bg-white rounded-lg mx-auto flex items-center justify-center">
                <QrCode className="h-24 w-24 text-black" />
              </div>
              <p className="text-gray-500 text-sm mt-2">Scan QR code to deposit</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setDepositModalOpen(false)}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Paper Deposit Modal */}
      <Dialog open={paperDepositModalOpen} onOpenChange={setPaperDepositModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <TestTube className="h-5 w-5 text-blue-400" />
              Add Paper Funds
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Add virtual {selectedAsset} to your paper trading wallet
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-gray-400 text-sm">Select Asset</label>
              <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                <SelectTrigger className="bg-[#1c1814] border-[#252540] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1814] border-[#252540]">
                  {cryptoAssets.map((asset) => (
                    <SelectItem key={asset.symbol} value={asset.symbol} className="text-white">
                      {asset.name} ({asset.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Amount</label>
              <Input
                type="number"
                value={paperDepositAmount}
                onChange={(e) => setPaperDepositAmount(e.target.value)}
                placeholder="Enter amount"
                className="bg-[#1c1814] border-[#252540] text-white mt-1"
              />
              <div className="flex gap-2 mt-2">
                {selectedAsset === 'USDT' ? (
                  [1000, 5000, 10000, 50000].map((amt) => (
                    <Button
                      key={amt}
                      variant="outline"
                      size="sm"
                      onClick={() => setPaperDepositAmount(String(amt))}
                      className="flex-1 border-[#252540] text-gray-400 hover:text-white hover:border-blue-500"
                    >
                      {amt.toLocaleString()}
                    </Button>
                  ))
                ) : (
                  [0.1, 0.5, 1, 5].map((amt) => (
                    <Button
                      key={amt}
                      variant="outline"
                      size="sm"
                      onClick={() => setPaperDepositAmount(String(amt))}
                      className="flex-1 border-[#252540] text-gray-400 hover:text-white hover:border-blue-500"
                    >
                      {amt}
                    </Button>
                  ))
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <TestTube className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <p className="text-blue-400 text-sm">
                This is virtual money for practice trading only. No real funds will be added.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline"
              onClick={() => setPaperDepositModalOpen(false)}
              className="border-gray-600 text-gray-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePaperDeposit}
              disabled={!paperDepositAmount}
              className="bg-blue-500 text-white hover:bg-blue-600"
            >
              Add Funds
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Withdraw Modal */}
      <Dialog open={withdrawModalOpen} onOpenChange={setWithdrawModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Minus className="h-5 w-5 text-[#D4AF37]" />
              Withdraw {selectedAsset}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Send {selectedAsset} to an external wallet
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-gray-400 text-sm">Asset</label>
              <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                <SelectTrigger className="bg-[#1c1814] border-[#252540] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1814] border-[#252540]">
                  {cryptoAssets.map((asset) => (
                    <SelectItem key={asset.symbol} value={asset.symbol} className="text-white">
                      {asset.name} ({asset.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Recipient Address</label>
              <Input
                value={withdrawAddress}
                onChange={(e) => setWithdrawAddress(e.target.value)}
                placeholder={`Enter ${selectedAsset} address`}
                className="bg-[#1c1814] border-[#252540] text-white mt-1"
              />
            </div>
            
            <div>
              <div className="flex justify-between mb-1">
                <label className="text-gray-400 text-sm">Amount</label>
                <span className="text-gray-500 text-sm">
                  Available: {formatCrypto(currentWallet?.balances?.[selectedAsset] || 0, selectedAsset)}
                </span>
              </div>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="bg-[#1c1814] border-[#252540] text-white"
                />
                <Button 
                  variant="outline"
                  onClick={() => setAmount(String(currentWallet?.balances?.[selectedAsset] || 0))}
                  className="border-[#D4AF37]/50 text-[#D4AF37]"
                >
                  Max
                </Button>
              </div>
            </div>
            
            <div className="p-4 bg-[#1c1814] rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Network Fee</span>
                <span className="text-white">~0.0005 {selectedAsset}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">You will receive</span>
                <span className="text-white">{amount || '0'} {selectedAsset}</span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline"
              onClick={() => setWithdrawModalOpen(false)}
              className="border-gray-600 text-gray-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleWithdraw}
              disabled={!amount || !withdrawAddress}
              className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
            >
              Withdraw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Buy Crypto Modal */}
      <Dialog open={buyModalOpen} onOpenChange={setBuyModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#D4AF37]" />
              Buy Crypto
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Purchase cryptocurrency with your card
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-gray-400 text-sm">I want to buy</label>
              <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                <SelectTrigger className="bg-[#1c1814] border-[#252540] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1814] border-[#252540]">
                  {cryptoAssets.map((asset) => (
                    <SelectItem key={asset.symbol} value={asset.symbol} className="text-white">
                      {asset.name} ({asset.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Pay with</label>
              <Select value={selectedFiat} onValueChange={setSelectedFiat}>
                <SelectTrigger className="bg-[#1c1814] border-[#252540] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1814] border-[#252540]">
                  {fiatCurrencies.map((currency) => (
                    <SelectItem key={currency.symbol} value={currency.symbol} className="text-white">
                      {currency.name} ({currency.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Amount ({selectedFiat})</label>
              <Input
                type="number"
                value={buyAmount}
                onChange={(e) => setBuyAmount(e.target.value)}
                placeholder="100.00"
                className="bg-[#1c1814] border-[#252540] text-white mt-1"
              />
              <div className="flex gap-2 mt-2">
                {[100, 250, 500, 1000].map((amt) => (
                  <Button
                    key={amt}
                    variant="outline"
                    size="sm"
                    onClick={() => setBuyAmount(String(amt))}
                    className="flex-1 border-[#252540] text-gray-400 hover:text-white hover:border-[#D4AF37]"
                  >
                    ${amt}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="p-4 bg-[#1c1814] rounded-lg">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">You will receive</span>
                <span className="text-white">
                  ~{buyAmount ? (parseFloat(buyAmount) / getAssetPrice(selectedAsset)).toFixed(8) : '0'} {selectedAsset}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Rate</span>
                <span className="text-[#D4AF37]">1 {selectedAsset} = {formatCurrency(getAssetPrice(selectedAsset))}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <Shield className="h-5 w-5 text-emerald-400 flex-shrink-0" />
              <p className="text-emerald-400 text-sm">
                Secure payment processed by our trusted payment partners
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline"
              onClick={() => setBuyModalOpen(false)}
              className="border-gray-600 text-gray-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBuyCrypto}
              disabled={!buyAmount}
              className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
            >
              Buy {selectedAsset}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Transfer/Swap Modal */}
      <Dialog open={transferModalOpen} onOpenChange={setTransferModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-blue-400" />
              Swap Assets
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Convert between different cryptocurrencies
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#1c1814] rounded-lg text-center">
              <p className="text-gray-400 text-sm mb-2">Swap feature coming soon</p>
              <p className="text-white">Convert between BTC, ETH, USDT and more</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setTransferModalOpen(false)}
              className="w-full bg-blue-500 text-white hover:bg-blue-600"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
